// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Certificates
// Browse, search, and print completion certificates with QR codes
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Text, Group, Stack, Table, Badge, TextInput,
  Button, Center, Loader, Select, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPrinter, IconCertificate,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBlackboardStore } from '../../store/blackboardStore';
import type { Certificate } from '../../store/blackboardStore';

const SITE_URL = window.location.origin;

function generateQRUrl(certCode: string): string {
  const verifyUrl = `${SITE_URL}/verify/${certCode}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(verifyUrl)}`;
}

export default function Certificates() {
  const {
    certificates, users, loading,
    loadCertificates, loadUsers, loadTrainings,
  } = useBlackboardStore();

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterTraining, setFilterTraining] = useState<string | null>(null);
  const [, setPrintCert] = useState<Certificate | null>(null);
  const [sortField, setSortField] = useState<string>('completedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (field: string) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  useEffect(() => {
    loadCertificates();
    loadUsers();
    loadTrainings();
  }, [loadCertificates, loadUsers, loadTrainings]);

  const userMap = useMemo(() => {
    const m: Record<string, string> = {};
    users.forEach(u => { m[String(u.id)] = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email; });
    return m;
  }, [users]);

  const enriched = useMemo(() => {
    return certificates.map(c => {
      const isExpired = c.expiresDate ? dayjs(c.expiresDate).isBefore(dayjs()) : false;
      const status = isExpired ? 'expired' : 'valid';
      return { ...c, isExpired, status, displayName: c.userName || userMap[c.userId] || 'Unknown' };
    });
  }, [certificates, userMap]);

  const filtered = useMemo(() => {
    const list = enriched.filter(c => {
      if (filterStatus === 'valid' && c.isExpired) return false;
      if (filterStatus === 'expired' && !c.isExpired) return false;
      if (filterTraining && c.trainingName !== filterTraining) return false;
      if (search) {
        const q = search.toLowerCase();
        return (c.displayName || '').toLowerCase().includes(q)
          || (c.trainingName || '').toLowerCase().includes(q)
          || (c.certCode || '').toLowerCase().includes(q);
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = (a as any)[sortField] || '';
      const vb = (b as any)[sortField] || '';
      return String(va).localeCompare(String(vb)) * dir;
    });
    setPage(1);
    return list;
  }, [enriched, search, filterStatus, filterTraining, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const trainingOptions = useMemo(() => {
    const names = [...new Set(enriched.map(c => c.trainingName).filter(Boolean))].sort();
    return names.map(n => ({ value: n, label: n }));
  }, [enriched]);

  const stats = useMemo(() => ({
    total: enriched.length,
    valid: enriched.filter(c => !c.isExpired).length,
    expired: enriched.filter(c => c.isExpired).length,
  }), [enriched]);

  const handlePrint = useCallback((cert: typeof enriched[0]) => {
    setPrintCert(cert);
    setTimeout(() => {
      const printContent = document.getElementById('cert-print-area');
      if (!printContent) return;
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.write(`<!DOCTYPE html><html><head><title>Certificate - ${cert.displayName}</title>
        <style>
          body { font-family: 'DM Sans', sans-serif; margin: 0; padding: 40px; }
          .cert-container { max-width: 700px; margin: 0 auto; border: 3px solid #1a365d; border-radius: 12px; padding: 48px; text-align: center; }
          .cert-title { font-size: 28px; font-weight: 700; color: #1a365d; margin-bottom: 8px; }
          .cert-subtitle { font-size: 14px; color: #64748b; margin-bottom: 32px; }
          .cert-name { font-size: 24px; font-weight: 700; color: var(--color-primary-800); margin: 16px 0; }
          .cert-training { font-size: 18px; font-weight: 600; color: #334e68; margin: 8px 0; }
          .cert-date { font-size: 14px; color: #64748b; margin: 4px 0; }
          .cert-code { font-family: monospace; font-size: 12px; color: #94a3b8; margin-top: 24px; }
          .qr-section { margin-top: 24px; }
          .qr-section img { width: 120px; height: 120px; }
          .qr-label { font-size: 10px; color: #94a3b8; margin-top: 4px; }
          @media print { body { padding: 20px; } }
        </style>
      </head><body>
        <div class="cert-container">
          <div class="cert-title">Certificate of Completion</div>
          <div class="cert-subtitle">OpenGov Emergency Management Training</div>
          <hr style="border:1px solid #e2e8f0;margin:16px 0;">
          <div style="font-size:13px;color:#64748b;">This certifies that</div>
          <div class="cert-name">${cert.displayName}</div>
          <div style="font-size:13px;color:#64748b;">has successfully completed</div>
          <div class="cert-training">${cert.trainingName}</div>
          <div class="cert-date">Completed: ${cert.completedDate ? dayjs(cert.completedDate).format('MMMM D, YYYY') : 'N/A'}</div>
          ${cert.expiresDate ? `<div class="cert-date">Expires: ${dayjs(cert.expiresDate).format('MMMM D, YYYY')}</div>` : ''}
          <div class="qr-section">
            <img src="${generateQRUrl(cert.certCode)}" alt="QR Code" />
            <div class="qr-label">Scan to verify</div>
            <div style="font-size:11px;color:#4a82c4;margin-top:6px;word-break:break-all;">${SITE_URL}/verify/${cert.certCode}</div>
          </div>
          <div class="cert-code">Certificate ID: ${cert.certCode}</div>
        </div>
      </body></html>`);
      win.document.close();
      win.onload = () => { win.print(); };
    }, 100);
  }, []);

  if (loading && certificates.length === 0) {
    return <Center py="xl"><Loader size="lg" /></Center>;
  }

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Text size="10px" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
            Training
          </Text>
          <Text size="xl" fw={700} c="#1a365d">Certificates</Text>
          <Text size="sm" c="dimmed" mt={4}>
            Browse, search, and print completion certificates with QR verification codes.
          </Text>
        </Box>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md" spacing="md">
        {[
          { label: 'Total Certificates', value: stats.total, color: '#4a82c4' },
          { label: 'Valid', value: stats.valid, color: '#16a34a' },
          { label: 'Expired', value: stats.expired, color: '#dc2626' },
        ].map(s => (
          <Paper key={s.label} p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${s.color}` }}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{s.label}</Text>
            <Text fz={28} fw={700} mt={2}>{s.value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Filters */}
      <Group gap="sm" mb="md">
        <TextInput
          placeholder="Search name, training, or code..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          placeholder="Status"
          data={[
            { value: 'valid', label: 'Valid' },
            { value: 'expired', label: 'Expired' },
          ]}
          value={filterStatus}
          onChange={setFilterStatus}
          clearable
          w={140}
        />
        <Select
          placeholder="Training"
          data={trainingOptions}
          value={filterTraining}
          onChange={setFilterTraining}
          clearable
          searchable
          w={220}
        />
      </Group>

      {/* Table */}
      {filtered.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconCertificate size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
            <Text c="dimmed">{certificates.length === 0 ? 'No certificates issued yet.' : 'No certificates match filters.'}</Text>
          </Stack>
        </Center>
      ) : (
        <Paper withBorder radius="sm" style={{ overflow: 'hidden' }}>
          <Table.ScrollContainer minWidth={700}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('displayName')}>Student{sortIcon('displayName')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('trainingName')}>Training{sortIcon('trainingName')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('completedDate')}>Completed{sortIcon('completedDate')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('expiresDate')}>Expires{sortIcon('expiresDate')}</Table.Th>
                  <Table.Th>Code</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map(cert => (
                  <Table.Tr key={cert.id}>
                    <Table.Td><Text size="sm" fw={500}>{cert.displayName}</Text></Table.Td>
                    <Table.Td><Text size="sm">{cert.trainingName}</Text></Table.Td>
                    <Table.Td><Text size="sm">{cert.completedDate ? dayjs(cert.completedDate).format('MMM D, YYYY') : '\u2014'}</Text></Table.Td>
                    <Table.Td><Text size="sm">{cert.expiresDate ? dayjs(cert.expiresDate).format('MMM D, YYYY') : 'Never'}</Text></Table.Td>
                    <Table.Td><Text size="xs" ff="monospace" c="dimmed">{cert.certCode}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={cert.isExpired ? 'red' : 'green'} size="sm">
                        {cert.isExpired ? 'Expired' : 'Valid'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconPrinter size={12} />}
                        onClick={() => handlePrint(cert)}
                      >
                        Print
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Box px="md" py="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2)', background: 'var(--mantine-color-gray-0)' }}>
            <Text size="xs" c="dimmed">Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} certificates</Text>
          </Box>
        </Paper>
      )}
      {totalPages > 1 && filtered.length > 0 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return (
              <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>
                {p}
              </Button>
            );
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      <div id="cert-print-area" style={{ display: 'none' }} />
    </Box>
  );
}
